import { Node } from "../node";

import { useVisitTypeStore } from "../../store/visit-type";

import { isEmpty } from "lodash";

export class SelectVisitTypeNode extends Node {
  get defaults() {
    return {
      ...super.defaults,
      name: "SelectVisitTypeNode",

      question: undefined,

      visitTypeIds: [],

      selectedVisitTypeId: undefined,
      targetNodeId: "continue",

      switchDecisionTree: false,
    };
  }

  get currentState() {
    return { selectedVisitTypeId: this.selectedVisitTypeId };
  }

  get context() {
    return { visitTypeId: this.selectedVisitTypeId, selectVisitTypeNodeShown: true };
  }

  get possibleNextNodeIds() {
    return [this.targetNodeId];
  }

  filteredVisitTypes(context) {
    const visibilityFilter = (visitType) => visitType.visible;
    const patientTypeFilter = (visitType) => visitType.validFor(context.patientType);

    const nodeLimitFilter = (visitType) => {
      if (isEmpty(this.visitTypeIds)) {
        return true;
      }

      return this.visitTypeIds.includes(visitType.id);
    };

    const providerLimitFilter = (visitType) => {
      if (isEmpty(context.providerVisitTypeIds)) {
        return true;
      }

      return context.providerVisitTypeIds.includes(visitType.id);
    };

    const { visitTypes } = useVisitTypeStore();
    const limitedVisitTypes = visitTypes
      .filter(visibilityFilter)
      .filter(patientTypeFilter)
      .filter(nodeLimitFilter)
      .filter(providerLimitFilter);

    const matchingVisitType = this.matchingProviderVisitType(
      context.visitType,
      context.patientType,
      context.providerVisitTypeIds,
    );

    if (matchingVisitType && !limitedVisitTypes.includes(matchingVisitType)) {
      limitedVisitTypes.push(matchingVisitType);
    }

    return limitedVisitTypes;
  }

  beforeShow(context) {
    if (this.shouldSkip(context)) {
      this.selectedVisitTypeId = context.visitTypeId;
    } else {
      this.selectedVisitTypeId = this.matchingProviderVisitType(
        context.visitType,
        context.patientType,
        context.providerVisitTypeIds,
      )?.id;
    }
  }

  autoAdvance(context) {
    return this.shouldSkip(context);
  }

  nextNodeId(context) {
    const nodeId = this.determineDecisionTreeSwitch(this.selectedVisitTypeId, context).nodeId;

    if (this.shouldSkip(context)) {
      return nodeId;
    }

    if (this.selectedVisitTypeId) {
      return nodeId;
    }
  }

  reset() {
    this.selectedVisitTypeId = undefined;
  }

  isDecisionTreeSwitch(context) {
    return this.determineDecisionTreeSwitch(this.selectedVisitTypeId, context).isSwitch;
  }

  // PRIVATE

  shouldSkip(context) {
    if (context.selectVisitTypeNodeShown && context.visitTypeId) {
      return true;
    }

    return !context.patientType || this.filteredVisitTypes(context).length === 1;
  }

  matchingProviderVisitType(visitType, patientType, providerVisitTypeIds) {
    if (!visitType || !patientType || !providerVisitTypeIds) {
      return undefined;
    }

    const { visitTypes } = useVisitTypeStore();

    // Start by finding all valid visit types that match the current visit type's name
    const matchingValidVisitTypes = visitTypes.filter(
      (v) => v.matchName === visitType.matchName && v.validFor(patientType),
    );

    // Option 1: Find a matching valid visit type that the provider has
    const matchingProviderVisitType = matchingValidVisitTypes.find((v) => providerVisitTypeIds.includes(v.id));

    // Option 2: Find a matching valid visit type that has the same specialty restrictions as the current visit type
    const matchingSpecialtyRestrictionVisitType = matchingValidVisitTypes.find((v) =>
      v.hasMatchingSpecialtyRestrictions(visitType),
    );

    // Option 3: Find a matching valid visit type that has no specialty restrictions
    const matchingNoSpecialtyRestrictionVisitType = matchingValidVisitTypes.find((v) => v.hasNoSpecialtyRestrictions());

    // Option 4: If we can't find a matching visit type, include the current visit type even though it isn't valid for this patientType
    // We'll show an error later if the user doesn't change the visit type
    const originalVisitType = visitType;

    return (
      matchingProviderVisitType ??
      matchingSpecialtyRestrictionVisitType ??
      matchingNoSpecialtyRestrictionVisitType ??
      originalVisitType
    );
  }
}
